import React from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface ThemeSwitcherProps {
    className?: string;
    showLabels?: boolean;
}

export default function ThemeSwitcher({ className = '', showLabels = false }: ThemeSwitcherProps) {
    const { theme, setTheme } = useTheme();

    const themes = [
        { id: 'light' as const, icon: Sun, label: 'Light' },
        { id: 'dark' as const, icon: Moon, label: 'Dark' },
        { id: 'system' as const, icon: Monitor, label: 'System' },
    ];

    return (
        <div className={`inline-flex items-center gap-1 p-1 bg-hig-bg-tertiary rounded-lg ${className}`}>
            {themes.map((t) => {
                const Icon = t.icon;
                const isActive = theme === t.id;

                return (
                    <button
                        key={t.id}
                        onClick={() => setTheme(t.id)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${isActive
                                ? 'bg-hig-bg-elevated text-hig-text-primary shadow-sm'
                                : 'text-hig-text-secondary hover:text-hig-text-primary'
                            }`}
                        title={t.label}
                    >
                        <Icon size={16} />
                        {showLabels && <span>{t.label}</span>}
                    </button>
                );
            })}
        </div>
    );
}

// Compact dropdown version for tight spaces
export function ThemeSwitcherDropdown({
    className = '',
    triggerClassName,
    triggerActiveClassName,
}: {
    className?: string;
    triggerClassName?: string;
    triggerActiveClassName?: string;
}) {
    const { theme, setTheme, resolvedTheme } = useTheme();
    const [isOpen, setIsOpen] = React.useState(false);

    const themes = [
        { id: 'light' as const, icon: Sun, label: 'Light' },
        { id: 'dark' as const, icon: Moon, label: 'Dark' },
        { id: 'system' as const, icon: Monitor, label: 'System' },
    ];

    const currentTheme = themes.find(t => t.id === theme) || themes[2];
    const CurrentIcon = currentTheme.icon;

    const defaultTriggerCls = "p-2 rounded-lg bg-hig-bg-tertiary hover:bg-hig-bg-elevated text-hig-text-secondary hover:text-hig-text-primary transition-colors";
    const baseCls = triggerClassName || defaultTriggerCls;
    const activeCls = isOpen && triggerActiveClassName ? `${baseCls} ${triggerActiveClassName}` : baseCls;

    return (
        <div className={`relative ${className}`}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={activeCls}
                title={`Theme: ${currentTheme.label}`}
            >
                <CurrentIcon size={18} />
            </button>

            {isOpen && (
                <>
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-36 py-1 bg-hig-bg-elevated border border-hig-border rounded-lg shadow-lg z-50">
                        {themes.map((t) => {
                            const Icon = t.icon;
                            const isActive = theme === t.id;

                            return (
                                <button
                                    key={t.id}
                                    onClick={() => {
                                        setTheme(t.id);
                                        setIsOpen(false);
                                    }}
                                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors ${isActive
                                            ? 'bg-hig-accent/10 text-hig-accent'
                                            : 'text-hig-text-secondary hover:text-hig-text-primary hover:bg-hig-bg-tertiary'
                                        }`}
                                >
                                    <Icon size={16} />
                                    <span>{t.label}</span>
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
